import React, { useState } from 'react';
import { Modal, Form, Input, Button, Table, Space, Tag, message, Spin } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import axiosApiInstance from '../../../api/axiosClient';

interface TrackSearchModalProps {
  visible: boolean;
  onClose: () => void;
  onSelectTrack: (track: API.MusicDistribution.ArtTrack) => void;
  releaseId: string;
}

const TrackSearchModal: React.FC<TrackSearchModalProps> = ({
  visible,
  onClose,
  onSelectTrack,
  releaseId,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<API.MusicDistribution.ArtTrack[]>([]);

  const parseCommaIdentifiers = (raw: string | undefined) => {
    if (!raw?.trim()) {
      return { isrc: undefined as string | undefined, upc: undefined as string | undefined };
    }
    const isrcs: string[] = [];
    const upcs: string[] = [];
    for (const part of raw.split(',')) {
      const token = part.trim();
      if (!token) continue;
      if (/^\d{8,14}$/.test(token)) {
        upcs.push(token);
      } else {
        isrcs.push(token);
      }
    }
    return {
      isrc: isrcs.length ? isrcs.join(',') : undefined,
      upc: upcs.length ? upcs.join(',') : undefined,
    };
  };

  const handleSearch = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      const appendCommonFilters = (params: URLSearchParams) => {
        if (values.track_id?.trim()) params.append('track_id', values.track_id.trim());
        if (values.title?.trim()) params.append('title', values.title.trim());
        if (values.release_title?.trim()) {
          params.append('release_title', values.release_title.trim());
        }
      };

      const bulkRaw = values.identifiers?.trim();
      let tracks: API.MusicDistribution.ArtTrack[] = [];

      if (bulkRaw) {
        const bulk = parseCommaIdentifiers(bulkRaw);
        const requestParams: URLSearchParams[] = [];
        if (bulk.isrc) {
          const p = new URLSearchParams();
          appendCommonFilters(p);
          p.append('isrc', bulk.isrc);
          requestParams.push(p);
        }
        if (bulk.upc) {
          const p = new URLSearchParams();
          appendCommonFilters(p);
          p.append('upc', bulk.upc);
          requestParams.push(p);
        }
        if (requestParams.length === 0) {
          message.info('Enter at least one ISRC or UPC in the bulk field');
          return;
        }
        const responses = await Promise.all(
          requestParams.map((p) =>
            axiosApiInstance.get(`/api/products/search/art_tracks?${p.toString()}`)
          )
        );
        const byId = new Map<string, API.MusicDistribution.ArtTrack>();
        for (const response of responses) {
          for (const track of response.data as API.MusicDistribution.ArtTrack[]) {
            byId.set(track.id, track);
          }
        }
        tracks = Array.from(byId.values());
      } else {
        const params = new URLSearchParams();
        appendCommonFilters(params);
        if (values.isrc?.trim()) params.append('isrc', values.isrc.trim());
        if (values.upc?.trim()) params.append('upc', values.upc.trim());
        const response = await axiosApiInstance.get(
          `/api/products/search/art_tracks?${params.toString()}`
        );
        tracks = response.data;
      }

      setSearchResults(tracks);

      if (tracks.length === 0) {
        message.info('No tracks found matching your search criteria');
      }
    } catch (error) {
      console.error('Error searching tracks:', error);
      message.error('Failed to search tracks');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTrack = (track: API.MusicDistribution.ArtTrack) => {
    onSelectTrack(track);
    onClose();
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'ISRC',
      dataIndex: 'isrc',
      key: 'isrc',
    },
    {
      title: 'Audio Source',
      key: 'audioSource',
      render: (_, record: API.MusicDistribution.ArtTrack) => {
        const sourceRelease = record.releases?.find(r => r.id === record.audio_file_release_id);
        return sourceRelease ? (
          <Tag color="blue">{sourceRelease.name}</Tag>
        ) : (
          record.filename ? <Tag color="green">Has Audio</Tag> : <Tag color="red">No Audio</Tag>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record: API.MusicDistribution.ArtTrack) => (
        <Button 
          type="primary" 
          onClick={() => handleSelectTrack(record)}
          disabled={record.audio_file_release_id === releaseId}
        >
          Select
        </Button>
      ),
    },
  ];

  return (
    <Modal
      title="Search Existing Tracks"
      open={visible}
      width={800}
      onCancel={onClose}
      footer={null}
    >
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSearch}
        >
          <Space style={{ width: '100%', marginBottom: 16 }} direction="vertical">
            <Form.Item
              label="ISRC or UPC (bulk)"
              name="identifiers"
              extra="Paste multiple values separated by commas. Numeric tokens are treated as UPC/EAN; others as ISRC."
            >
              <Input.TextArea
                rows={2}
                placeholder="USRC12345678, USRC87654321 or 123456789012, 987654321098"
              />
            </Form.Item>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <Form.Item label="Track ID" name="track_id">
                <Input placeholder="Enter track ID" />
              </Form.Item>
              <Form.Item label="Title" name="title">
                <Input placeholder="Enter track title" />
              </Form.Item>
              <Form.Item
                label="ISRC"
                name="isrc"
                extra="One or more ISRCs, comma-separated"
              >
                <Input placeholder="e.g. USXXX1234567, USYYY7654321" />
              </Form.Item>
              <Form.Item
                label="UPC"
                name="upc"
                extra="One or more UPC/EAN values, comma-separated"
              >
                <Input placeholder="e.g. 123456789012, 987654321098" />
              </Form.Item>
              <Form.Item label="Release Title" name="release_title">
                <Input placeholder="Enter release title" />
              </Form.Item>
            </div>
            <Button 
              type="primary" 
              icon={<SearchOutlined />} 
              onClick={handleSearch}
              style={{ marginBottom: 16 }}
            >
              Search
            </Button>
          </Space>
        </Form>

        <Table
          dataSource={searchResults}
          columns={columns}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Spin>
    </Modal>
  );
};

export default TrackSearchModal;